import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  CalendarDays,
  Clock,
  AlertTriangle,
  CheckCircle,
  FileText,
  Building2,
  ChevronLeft,
  ChevronRight,
  Bell
} from "lucide-react";
import { toast } from "sonner";
import { format, addDays, isBefore, isAfter, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CalendarPage() {
  const [loading, setLoading] = useState(true);
  const [deadlines, setDeadlines] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [eventDetailModal, setEventDetailModal] = useState({ open: false, event: null });

  useEffect(() => {
    fetchDeadlines();
  }, [selectedYear]);

  const fetchDeadlines = async () => {
    setLoading(true);
    try {
      // Fetch various deadlines
      const [settingsRes, bilansRes, enterprisesRes] = await Promise.all([
        axios.get(`${API_URL}/settings`).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/bilans`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/enterprises`).catch(() => ({ data: [] }))
      ]);

      const events = [];
      
      // 1. Global deadline from settings
      const globalDeadline = settingsRes.data?.global_deadline;
      if (globalDeadline) {
        events.push({
          id: 'global-deadline',
          type: 'deadline',
          title: `Date limite de dépôt des bilans ${selectedYear}`,
          date: `${selectedYear}-${globalDeadline}`,
          description: `Date limite globale pour le dépôt des bilans sociaux de l'année ${selectedYear}`,
          severity: 'critical',
          icon: 'calendar'
        });
      } else {
        // Default deadline: March 31
        events.push({
          id: 'default-deadline',
          type: 'deadline',
          title: `Date limite de dépôt des bilans ${selectedYear}`,
          date: `${selectedYear + 1}-03-31`,
          description: `Date limite par défaut (31 mars) pour les bilans de l'année ${selectedYear}`,
          severity: 'critical',
          icon: 'calendar'
        });
      }

      // 2. Bilans in draft or pending status
      const bilans = bilansRes.data || [];
      bilans.forEach(bilan => {
        if (bilan.status === 'brouillon' || bilan.status === 'soumis') {
          const deadline = bilan.date_limite || `${bilan.annee + 1}-03-31`;
          events.push({
            id: `bilan-${bilan.id}`,
            type: 'bilan',
            title: `Bilan ${bilan.enterprise_name || 'Entreprise'} - ${bilan.annee}`,
            date: deadline,
            description: `Statut: ${bilan.status === 'brouillon' ? 'Brouillon' : 'Soumis'}`,
            severity: bilan.status === 'brouillon' ? 'warning' : 'info',
            icon: 'file',
            bilan_id: bilan.id,
            enterprise_name: bilan.enterprise_name
          });
        }
        
        // Penalty payment deadlines
        if (bilan.penalite && bilan.penalite.montant > 0 && !bilan.penalite.payee) {
          events.push({
            id: `penalty-${bilan.id}`,
            type: 'penalty',
            title: `Pénalité à payer - ${bilan.enterprise_name || 'Entreprise'}`,
            date: bilan.penalite.date_limite_paiement || addDays(new Date(), 30).toISOString().split('T')[0],
            description: `Montant: ${new Intl.NumberFormat('fr-FR').format(bilan.penalite.montant)} GNF`,
            severity: 'critical',
            icon: 'alert',
            bilan_id: bilan.id
          });
        }
      });

      // 3. Enterprise registration anniversaries (for renewal reminders)
      const enterprises = enterprisesRes.data || [];
      enterprises.forEach(enterprise => {
        if (enterprise.created_at) {
          const createdDate = new Date(enterprise.created_at);
          const anniversaryThisYear = new Date(selectedYear, createdDate.getMonth(), createdDate.getDate());
          events.push({
            id: `anniversary-${enterprise.id}`,
            type: 'reminder',
            title: `Anniversaire enregistrement - ${enterprise.raison_sociale}`,
            date: anniversaryThisYear.toISOString().split('T')[0],
            description: `${selectedYear - createdDate.getFullYear()} an(s) depuis l'enregistrement`,
            severity: 'info',
            icon: 'building',
            enterprise_id: enterprise.id
          });
        }
      });

      setDeadlines(events);
    } catch (error) {
      console.error(/* PROD */ "Error fetching deadlines:", error);
      toast.error("Erreur lors du chargement du calendrier");
    } finally {
      setLoading(false);
    }
  };

  const getEventsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return deadlines.filter(event => {
      try {
        const eventDate = event.date?.split('T')[0];
        return eventDate === dateStr;
      } catch {
        return false;
      }
    });
  };

  const getUpcomingEvents = () => {
    const now = new Date();
    const upcoming = deadlines
      .filter(event => {
        try {
          const eventDate = parseISO(event.date);
          return isAfter(eventDate, now) || isSameDay(eventDate, now);
        } catch {
          return false;
        }
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 10);
    return upcoming;
  };

  const getOverdueEvents = () => {
    const now = new Date();
    return deadlines.filter(event => {
      try {
        const eventDate = parseISO(event.date);
        return isBefore(eventDate, now) && !isSameDay(eventDate, now);
      } catch {
        return false;
      }
    }).filter(e => e.type === 'deadline' || e.type === 'penalty' || (e.type === 'bilan' && e.severity === 'warning'));
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-500';
      case 'warning': return 'bg-amber-500';
      case 'info': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case 'critical': return <Badge className="bg-red-100 text-red-800">Critique</Badge>;
      case 'warning': return <Badge className="bg-amber-100 text-amber-800">Attention</Badge>;
      case 'info': return <Badge className="bg-blue-100 text-blue-800">Info</Badge>;
      default: return <Badge variant="secondary">Normal</Badge>;
    }
  };

  const getEventIcon = (icon) => {
    switch (icon) {
      case 'calendar': return <CalendarDays className="w-4 h-4" />;
      case 'file': return <FileText className="w-4 h-4" />;
      case 'alert': return <AlertTriangle className="w-4 h-4" />;
      case 'building': return <Building2 className="w-4 h-4" />;
      default: return <Bell className="w-4 h-4" />;
    }
  };

  // Custom day render for calendar
  const getDayContent = (day) => {
    const events = getEventsForDate(day);
    if (events.length === 0) return null;
    
    const hasCritical = events.some(e => e.severity === 'critical');
    const hasWarning = events.some(e => e.severity === 'warning');
    
    return (
      <div className="flex justify-center mt-1 gap-0.5">
        {hasCritical && <div className="w-1.5 h-1.5 rounded-full bg-red-500" />}
        {hasWarning && <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />}
        {!hasCritical && !hasWarning && <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
      </div>
    );
  };

  const upcomingEvents = getUpcomingEvents();
  const overdueEvents = getOverdueEvents();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B4D3E]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarDays className="w-6 h-6 text-[#1B4D3E]" />
            Calendrier des Échéances
          </h1>
          <p className="text-gray-500 mt-1">
            Suivez les dates limites et les événements importants
          </p>
        </div>
        
        <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
          <SelectTrigger className="w-[120px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[2023, 2024, 2025, 2026, 2027].map(year => (
              <SelectItem key={year} value={String(year)}>{year}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Alert for overdue items */}
      {overdueEvents.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-red-800">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-semibold">{overdueEvents.length} échéance(s) dépassée(s)</span>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Calendrier {selectedYear}</CardTitle>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              month={selectedMonth}
              onMonthChange={setSelectedMonth}
              locale={fr}
              className="rounded-md border"
              components={{
                DayContent: ({ date }) => (
                  <div className="relative">
                    <span>{format(date, 'd')}</span>
                    {getDayContent(date)}
                  </div>
                )
              }}
            />
            
            {/* Events for selected date */}
            {selectedDate && (
              <div className="mt-4 pt-4 border-t">
                <h4 className="font-semibold mb-2">
                  Événements du {format(selectedDate, 'd MMMM yyyy', { locale: fr })}
                </h4>
                {getEventsForDate(selectedDate).length > 0 ? (
                  <div className="space-y-2">
                    {getEventsForDate(selectedDate).map(event => (
                      <div 
                        key={event.id}
                        className={`p-3 rounded-lg border cursor-pointer hover:shadow-md transition-shadow ${
                          event.severity === 'critical' ? 'border-red-200 bg-red-50' :
                          event.severity === 'warning' ? 'border-amber-200 bg-amber-50' :
                          'border-blue-200 bg-blue-50'
                        }`}
                        onClick={() => setEventDetailModal({ open: true, event })}
                      >
                        <div className="flex items-center gap-2">
                          {getEventIcon(event.icon)}
                          <span className="font-medium">{event.title}</span>
                          {getSeverityBadge(event.severity)}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Aucun événement ce jour</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Events Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#1B4D3E]" />
                Prochaines échéances
              </CardTitle>
            </CardHeader>
            <CardContent>
              {upcomingEvents.length > 0 ? (
                <div className="space-y-3">
                  {upcomingEvents.map(event => (
                    <div 
                      key={event.id}
                      className={`p-3 rounded-lg border-l-4 cursor-pointer hover:bg-gray-50 ${
                        event.severity === 'critical' ? 'border-l-red-500' :
                        event.severity === 'warning' ? 'border-l-amber-500' :
                        'border-l-blue-500'
                      }`}
                      onClick={() => setEventDetailModal({ open: true, event })}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm truncate">{event.title}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <CalendarDays className="w-3 h-3" />
                        {format(parseISO(event.date), 'd MMM yyyy', { locale: fr })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                  <p className="text-sm">Aucune échéance à venir</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Legend */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Légende</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span>Critique (dates limites, pénalités)</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-3 h-3 rounded-full bg-amber-500" />
                <span>Attention (bilans en brouillon)</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span>Information (rappels, anniversaires)</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Event Detail Modal */}
      <Dialog open={eventDetailModal.open} onOpenChange={(open) => setEventDetailModal({ open, event: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {eventDetailModal.event && getEventIcon(eventDetailModal.event.icon)}
              {eventDetailModal.event?.title}
            </DialogTitle>
          </DialogHeader>
          {eventDetailModal.event && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                {getSeverityBadge(eventDetailModal.event.severity)}
                <span className="text-sm text-gray-500">
                  {format(parseISO(eventDetailModal.event.date), 'd MMMM yyyy', { locale: fr })}
                </span>
              </div>
              <p className="text-gray-700">{eventDetailModal.event.description}</p>
              {eventDetailModal.event.bilan_id && (
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => window.location.href = `/bilans/${eventDetailModal.event.bilan_id}`}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Voir le bilan
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
